// React
import React, { forwardRef, useEffect, useState, useImperativeHandle, createRef } from 'react';
// External
import { Col, ListGroup, Row } from 'react-bootstrap';
// Internal
import ConfirmationTable from 'components/shared/organisms/ConfirmationTable/ConfirmationTable';
import HeaderDivider from 'components/shared/atoms/HeaderDivider/HeaderDivider';
import OrderTable from 'components/shared/organisms/OrderTable/OrderTable';
import CustomTable from 'components/shared/molecules/CustomTable/CustomTable';
import ProductService from 'services/product';
// Styles
import './styles.scss';
import Vet from 'models/Vet';
import { useScreenshot } from "use-react-screenshot";

const BookingDetails = forwardRef(({ booking, vetDetails, headerTitle }, ref) => {
    // Create a local ref for this component
    const localRef = createRef(null);
    const [ products, setProducts ] = useState(booking.getProductsTableFormat());
    const [image, takeScreenShot] = useScreenshot();
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const vet = Vet(vetDetails);

    // Expose the getImage function to the passed prop ref from the parent component 
    useImperativeHandle(ref, () => ({
        /**
         * Take a screenshot and then return the image
         * @returns {Promise}
         */
        async getImage() {
            if (localRef.current) {
                return takeScreenShot(localRef.current).then((res) => {
                    return res;
                });
            } else {
                return null;
            }            
        }
    }));

    let submittedDate = booking.orderSubmitted ? "Booking Submitted" + ' ' + booking.orderSubmitted.tz(timeZone).format('hh:mma, ddd D MMM YYYY') : ''
    useEffect(async () => {
        const items = booking.getQuotedProducts();
        let collectionFee = parseFloat(booking.collection_fee);
        let returnFee = parseFloat(booking.return_fee);


        // Get the product prices for each product and replace each product's price with it
        if (vet.getPricingOption() !== 'No price') {
            for (const item of items) {
                let response = await ProductService.getProductDetail(item.id, item.producttemplateIdC);
                item.unitPrice = parseFloat((`${response.data.total_product_price}`).replace(/,/g, ''));
            }; 
        }        

        // Add the travel costs to the order table 
        items.push(...[
            {
                quantity: 1,
                unitPrice: collectionFee,
                name: 'Collection Travel Cost',
                engravingText: null,
                childProductsCartFormat: null,
            },
            {
                quantity: 1,
                unitPrice: returnFee,
                name: 'Return Travel Cost',
                engravingText: null,
                childProductsCartFormat: null,
            },
        ]);

        setProducts(booking.getProductsTableFormat(items));
    }, [vetDetails, booking]);
    if (!booking) {
        return null;
    }

    return (
        <div className="booking-details-body" ref={localRef}>
        <img hidden src={image} />
            <div className="booking-container">
                <Row>
                    <h3 className='step-title stepper-title-margin'>{headerTitle}</h3>
                </Row>
                <Row>
                    <Col>
                        <div className="confirmation-table-container">
                            <div>
                                <h4> {submittedDate} </h4>
                                <hr className="table-divider" />
                            </div>
                            <CustomTable
                                className="booking-confirmation-status-table"
                                data={booking ? booking.getStatus(vet, booking) : []}
                                headers={[]}
                            />
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <ConfirmationTable
                            data={booking ? booking.getServiceDetails(vet) : []}
                            headerTitle="Service Details"
                            className="service-details"
                        />
                    </Col>
                </Row>
                {
                    booking.owner.firstName || booking.pet.firstName
                        ? (
                            <Row xs={1} md={2} lg={2}>
                                <Col>
                                    <ConfirmationTable
                                        data={booking ? booking.getOwnerDetails() : []}
                                        headerTitle="Owner details"
                                        className="owner-details"
                                    />
                                </Col>
                                <Col>
                                    <ConfirmationTable
                                        data={booking ? booking.getPetDetails() : []}
                                        headerTitle="Pet details"
                                        className="pet-details"
                                    />
                                </Col>
                            </Row>
                        ) : null
                }
                {
                    booking.specialInstructions.length > 0
                        ? (
                            <div className="mt-40">
                                <HeaderDivider title="Special instructions" />
                                <ListGroup>
                                    {booking.specialInstructions.map((item, index) => (
                                        <ListGroup.Item key={item.id}>
                                            {`${index + 1}. ${item.description}`}
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            </div>
                        ) : null
                }
                {
                    booking.quotedProducts.length > 0
                        ? (
                            <div className="mt-40">
                                <OrderTable
                                    data={products}
                                    headerTitle="Order"
                                    vet={vet}
                                />
                            </div>
                        ) : null
                }
            </div>
        </div>
    );
});

export default BookingDetails;
